import View from '@/components/view/View';
import * as R from '@/constants/systemRoutes';
import { getOnStorage, setOnStorage } from '@/shared';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Header, CheckoutProgressBar } from '@/components';
import { useCart, useClient, useUserContext, useGlobals, useRemoteConfig } from '@/hooks';

interface Props {
  children: any;
}

// milisegundos * segundos * minutos * horas * dias = 30 dias pois é o periodo de consulta das mensagens
const MAX_TIME_TO_KEEP_RECADOS = 1000 * 60 * 60 * 24 * 30;

const doc = document as any;
const workerUrl =
  'data:application/javascript;base64,' +
  btoa(`
  self.addEventListener('message', (e) => {
    if(e.data==='hello'){
      self.postMessage('hello');
    }
    debugger;
    self.postMessage('');
  });
`);

const enableValidationInspect = false; /* ![
  "localhost",
  "d1864eisj85olf.cloudfront.net",
  "pwa-rca-dev.grupopereira.link",
  "192.168.15.27",
  "192.168.1.13",
].includes(window.location.hostname); */

const { userAgent } = navigator;
const tabletRegex = /iPad|tablet|playbook|silk|Macintosh|Windows|Linux|X11/i;
const mobileRegex = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
const isMobile = mobileRegex.test(userAgent) && !tabletRegex.test(userAgent);

const keepAliveRoutes = [R.ECOMMERCE.link, R.SEARCH.link, R.CATEGORY.link];
const SHOW_FLAG = window.agp_config.REACT_APP_AWS.includes('prod') && !!window.agp_config.SHOW_AMBIENT_FLAG;

const checkIfDebuggerEnabled = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    let fulfilled = false;
    const worker = new Worker(workerUrl);
    worker.onmessage = (e) => {
      const data = e.data;
      if (data === 'hello') {
        setTimeout(() => {
          if (!fulfilled) {
            resolve(true);
            worker.terminate();
          }
        }, 100);
      } else {
        fulfilled = true;
        resolve(false);
        worker.terminate();
      }
    };
    worker.postMessage('hello');
  });
};

const Container = ({ children }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStarted = useRef(false);
  const { isSearching } = useCart();
  const scrollPositionRef = useRef(0);
  const { signOut } = useUserContext();
  const { selectedClient } = useClient();
  const { enableScrollHide } = useRemoteConfig();
  const isStartedCheckIfIsEnabled = useRef(false);
  const { scrollPosition, setScrollDirection } = useGlobals();

  useEffect(() => {
    if (!enableScrollHide) return;

    const items = document.querySelectorAll('.scroll-down, .scroll-up');
    const handleScroll = () => {
      // const diff = Math.abs(window.scrollY - scrollPositionRef.current);
      if (scrollPositionRef.current === window.scrollY/*  || window.scrollY < 300 || diff < 50 */) return;

      if (scrollPositionRef.current < window.scrollY) {
        setScrollDirection('down');
        items.forEach((item) => {
          item.classList.add('is-scrolled');
        });
      } else {
        setScrollDirection('up');
        items.forEach((item) => {
          item.classList.remove('is-scrolled');
        });
      }

      scrollPositionRef.current = window.scrollY;
    };

    const initTimeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 600);

    return () => {
      clearTimeout(initTimeout);
      window.removeEventListener('scroll', handleScroll);
      scrollPositionRef.current = 0;
    };
  }, [enableScrollHide, setScrollDirection]);

  useEffect(() => {
    if (!location.pathname.includes('login')) {
      setOnStorage('last_route', location.pathname);
    }

    const root = document.getElementById('root');
    if (root) root.classList.add('enter-efect');

    const saved = scrollPosition;

    const isDashboardRoute = location.pathname.includes(R.DASHBOARD.link);
    const isKeepAliveRoute = keepAliveRoutes.some((route) => location.pathname.startsWith(route));

    // DASHBOARD ainda respeita o scroll salvo global
    if (isDashboardRoute) {
      if (saved > 0) {
        setTimeout(() => {
          window.scrollTo({
            top: saved,
            behavior: 'smooth',
          });
        }, 200);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (!isKeepAliveRoute) {
      // para rotas que NÃO usam KeepAlive, sempre volta pro topo
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 200);
    }
    // para rotas keep-alive (ECOMMERCE, SEARCH, CATEGORIE) não fazemos nada aqui:
    // o scroll fica por conta do DOM cacheado pelo KeepAlive

    const removeClassTimeout = setTimeout(() => {
      if (root) root.classList.remove('enter-efect');
    }, 2000);

    return () => {
      clearTimeout(removeClassTimeout);
    };
  }, [location.pathname, scrollPosition]);

  useEffect(() => {
    const isFirstLoad = getOnStorage('is_first_load');
    if (isFirstLoad) return;

    navigator.serviceWorker
      ?.getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => {
          registration
            .unregister()
            .then(() => {
              document.cookie = 'Clear-Site-Data: "cache", "cookies", "executionContexts"';
              setOnStorage('is_first_load', 'true');
              window.location.reload();
            })
            .catch((error) => {
              console.error('Service worker unregistration failed:', error);
            });
        });
      })
      .catch((error) => {
        console.error('Service worker registration retrieval failed:', error);
      });
  }, []);

  const checkIfIsEnabled = useCallback(
    (count: number) => {
      if (isStartedCheckIfIsEnabled.current) return;

      const enabledRoutes = [
        R.LOGIN.link,
        R.COMPANY.link,
        R.DASHBOARD.link,
        R.MY_CLIENTS.link,
        R.SALES_CYCLE.link,
        R.USER_DATA.link,
        R.ORDER_STATUS_PAGE.link,
        R.PRODUCTIVITY_EXTRACT.link,
        R.INDICATORS.link,
        R.MESSAGES.link,
        R.MESSAGE.link,
        R.SALES_REPORT.link,
        R.NEW_CLIENT.link,
        R.PERFORMANCE_INDICATORS.link,
      ];

      const isEnabledRoutes = enabledRoutes.some((route) => {
        if (location?.pathname === route) return true;

        if (route.includes(':')) {
          const routeBase = route.replace(/\/:[^/]+/g, '');
          return location?.pathname?.startsWith(routeBase + '/');
        }

        return false;
      });

      if (count === 5) return navigate(R.DASHBOARD.link);
      if (selectedClient?.COD_CLIENTE || isEnabledRoutes) return (isStartedCheckIfIsEnabled.current = true);

      setTimeout(() => {
        checkIfIsEnabled(count + 1);
      }, 1000);
    },
    [selectedClient?.COD_CLIENTE, location?.pathname, navigate]
  );

  useEffect(() => {
    checkIfIsEnabled(0);
  }, [selectedClient?.COD_CLIENTE, checkIfIsEnabled]);

  const validate = useCallback(() => {
    if (!isMobile && location.pathname !== R.LOGIN.link && enableValidationInspect) {
      const logout = () => {
        if (location.pathname === R.LOGIN.link) return;

        signOut();
        navigate(R.LOGIN.link);
      };

      setInterval(async () => {
        const result = await checkIfDebuggerEnabled();
        if (result) logout();
      }, 4000);

      const bloquearCliqueDireito = () => {
        if (doc.addEventListener) {
          doc.addEventListener('contextmenu', (e: any) => {
            e.preventDefault();
            return false;
          });
        } else {
          doc.attachEvent('oncontextmenu', (e: any) => {
            e = e || window.event;
            e.returnValue = false;
            return false;
          });
        }
      };

      const bloquearBotoesInspect = () => {
        doc.addEventListener('keydown', (e: any) => {
          const key = e.keyCode;
          const isAlt = !!e.altKey;
          const isCtrl = !!e.ctrlKey;
          const isMeta = !!e.metaKey;
          const isShift = !!e.shiftKey;
          const invalidKey = key === 73 || key === 74 || key === 67;

          const isInspect =
            key === 123 ||
            (isCtrl && key === 85) ||
            (isCtrl && isShift && key === 75) ||
            (isMeta && isAlt && invalidKey) ||
            (isMeta && isAlt && invalidKey) ||
            (isMeta && isShift && invalidKey) ||
            (isCtrl && isShift && invalidKey) ||
            (isMeta && key === 85);

          if (isInspect) e.preventDefault();
        });
      };

      bloquearBotoesInspect();
      bloquearCliqueDireito();
    }
  }, [location.pathname, navigate, signOut]);

  const validateRecados = useCallback(() => {
    const recados = getOnStorage('recados');
    const now = new Date();
    if (!!recados) {
      Object.keys(recados).forEach((key) => {
        const recadoDate = new Date(recados[Number(key)]);
        const diffInMilliseconds = now.getTime() - recadoDate.getTime();
        if (diffInMilliseconds > MAX_TIME_TO_KEEP_RECADOS) {
          delete recados[Number(key)];
        }
      });
      setOnStorage('recados', recados);
    }
  }, []);

  useEffect(() => {
    if (isStarted.current) return;
    isStarted.current = true;

    validate();
    validateRecados();
  }, [validate]);

  return (
    <View>
      <Header
        routeLabelSize={location.pathname === R.ORDER_STATUS_PAGE.link ? 16 : 0}
        rightIcon={null}
        stickChild={location.pathname === R.CART.link && !isSearching && <CheckoutProgressBar />}
      />

      {SHOW_FLAG && (
        <div style={{ position: 'fixed', marginLeft: 120, top: 0, left: '50%', transform: 'translateX(-50%)', width: 'fit-content', backgroundColor: '#00ff00', zIndex: 9999999999, padding: '5px 5px 2px 5px', borderBottomRightRadius: 4, borderBottomLeftRadius: 4 }}>
          <p style={{ fontSize: 9, fontWeight: 900, color: 'black' }}>PROD</p>
        </div>
      )}

      {children}
      <Menu />
    </View>
  );
};

export default Container;
